import { Box, Tooltip } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { getTravelDocumentKindsOptions, useTravelRequestComplete } from '../model/TravelRequestQuery';
import {
  TravelRequest,
  hasTravelMobilitySupport,
  missingTravelReturnKinds,
  travelFileKindLabel,
  travelReturnGroupLabel,
  unsatisfiedTravelReturnGroups,
} from '../model/TravelRequestTypes';
import { TravelRequestConfirmActionButton } from './TravelRequestConfirmActionButton';

interface TravelRequestCompleteButtonProps {
  request: TravelRequest;
}

export function TravelRequestCompleteButton({ request }: TravelRequestCompleteButtonProps) {
  const { t } = useTranslation();
  const mutation = useTravelRequestComplete(request.id);
  const { data } = useQuery(getTravelDocumentKindsOptions(request.activityType, hasTravelMobilitySupport(request)));

  if (!request.canComplete) {
    return null;
  }

  // Only a known gap disables the button; while the kinds load or fail, the server's own check answers.
  const missing = data == null ? [] : missingTravelReturnKinds(request, data);
  const openGroups = data == null ? [] : unsatisfiedTravelReturnGroups(request, data);
  const disabled = missing.length > 0 || openGroups.length > 0;
  const contentKey = request.activityType === 'ERASMUS' ? 'contentErasmus' : 'content';
  const reason = [
    t('travel.requests.complete.missingReturnDocuments'),
    [
      ...missing.map(kind => travelFileKindLabel(t, kind, request.activityType)),
      ...openGroups.map(group => travelReturnGroupLabel(t, group, request.activityType)),
    ].join(', '),
  ].join(': ');

  // A disabled button emits no mouse events, so the tooltip hangs off a wrapper instead.
  return (
    <Tooltip
      label={reason}
      disabled={!disabled}
      events={{ hover: true, focus: true, touch: true }}
      multiline
      w={280}
    >
      <Box
        component="span"
        display="inline-block"
        data-disabled={disabled || undefined}
      >
        <TravelRequestConfirmActionButton
          label={t('travel.requests.actions.complete')}
          confirmTitle={t('travel.requests.actions.confirmComplete.title')}
          confirmContent={t(`travel.requests.actions.confirmComplete.${contentKey}`)}
          disabled={disabled}
          mutation={mutation}
        />
      </Box>
    </Tooltip>
  );
}
